import axios from 'axios';
import { useEffect, useRef, useCallback } from 'react';

import { decodeJwtUnsafe } from './govbr';

interface TokenRefreshManagerProps {
  children: React.ReactNode;
}

const TokenRefreshManager: React.FC<TokenRefreshManagerProps> = ({
  children,
}) => {
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  const refreshToken = useCallback(async () => {
    if (isRefreshingRef.current) {
      //   console.log('Refresh já em andamento, aguardando...');
      return;
    }

    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      //  console.warn('Nenhum refresh_token encontrado');
      return;
    }

    isRefreshingRef.current = true;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/refresh`,
        { refresh_token },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      const { access_token, refresh_token: newRefresh } = response.data;

      localStorage.setItem('token', access_token);
      if (newRefresh) {
        localStorage.setItem('refresh_token', newRefresh);
      }

      // Reagendar o próximo refresh
      scheduleTokenRefresh(access_token);
    } catch (error: any) {
      console.error(' Erro ao renovar token:', error);

      // Se o refresh falhar, limpar tokens e redirecionar para login
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        //  console.warn('Refresh token inválido. Redirecionando...');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/';
      }
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  const scheduleTokenRefresh = useCallback(
    (token: string) => {
      // Limpar timeout anterior
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      try {
        const payload = decodeJwtUnsafe(token);

        if (!payload?.exp) {
          console.warn(' Token sem campo exp');
          return;
        }

        const expiresAt = payload.exp * 1000; // Converter para milissegundos
        const now = Date.now();

        // Calcular quanto tempo falta para expirar
        const timeUntilExpiry = expiresAt - now;

        // Renovar 2 minutos antes de expirar (120 segundos)
        const refreshBeforeExpiry = 60 * 1000; // 1 minutos em ms
        const timeoutDuration = timeUntilExpiry - refreshBeforeExpiry;

        if (timeoutDuration <= 0) {
          //  console.warn(' Token expirado ou prestes a expirar. Renovando...');
          refreshToken();
        } else {
          // const minutesUntilRefresh = Math.floor(timeoutDuration / 1000 / 60);
          // const secondsUntilRefresh = Math.floor((timeoutDuration / 1000) % 60);

          // console.log(
          //   ` Próximo refresh em ${minutesUntilRefresh}min ${secondsUntilRefresh}s`,
          // );

          refreshTimeoutRef.current = setTimeout(() => {
            refreshToken();
          }, timeoutDuration);
        }
      } catch (error) {
        console.error(' Erro ao agendar refresh:', error);
      }
    },
    [refreshToken],
  );

  // Inicializar o agendamento quando o componente montar
  useEffect(() => {
    const token = localStorage.getItem('token');

    if (token) {
      // console.log('Inicializando gerenciador de refresh');
      scheduleTokenRefresh(token);
    }

    // Limpar timeout ao desmontar
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [scheduleTokenRefresh]);

  // Monitorar mudanças no localStorage (sincronizar entre abas)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' && e.newValue) {
        scheduleTokenRefresh(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [scheduleTokenRefresh]);

  return <>{children}</>;
};

export default TokenRefreshManager;
